/**
 * Admin auth context — keeps the current admin user in React state.
 *
 * All admin API calls use `axios` with `withCredentials: true` so the
 * httpOnly cookies set by the backend ride along automatically. We never
 * store the token in JS-readable storage (no localStorage / sessionStorage)
 * — that's the whole point of httpOnly.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_ROOT } from '../lib/api';

// Shared axios client for the admin area — credentials always on.
export const adminApi = axios.create({
  baseURL: API_ROOT,
  withCredentials: true,
  timeout: 20000,
});

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/admin/me');
      setAdmin(data);
      return data;
    } catch {
      setAdmin(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await adminApi.post('/admin/logout');
    } finally {
      setAdmin(null);
    }
  }, []);

  const value = { admin, setAdmin, loading, refresh, logout };
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
